/* Implements Warm Academic theme (Option 2 - Khan Academy & Coursera feel) alongside platform themes */
import { useState, useEffect } from 'react';
import { SponsoredService } from '../services/sponsoredService';

export interface ThemeTokens {
  theme: string;
  isClinical: boolean;
  isWarmAcademic: boolean;
  isStructuredCourse: boolean;
  isMinimal: boolean;
  isHighContrast: boolean;
  isVibrant: boolean;
  isDefault: boolean;
  
  // Style class generators
  primaryBtn: string;
  secondaryBtn: string;
  activeTab: string;
  inactiveTab: string;
  cardBg: string;
  headerFont: string;
  badge: string;
  progressBar: string;
  accentText: string;
  accentBorder: string;
  heroBannerBg: string;
  cardHeaderBg: string;
  iconBg: string;
}

export function getThemeTokens(themeName: string): ThemeTokens {
  const theme = themeName || 'default';
  const isClinical = theme === 'clinical_trust';
  const isWarmAcademic = theme === 'warm_academic';
  const isStructuredCourse = theme === 'structured_course' || theme === 'structured_course_platform';
  const isMinimal = theme === 'modern_minimal';
  const isHighContrast = theme === 'high_contrast';
  const isVibrant = theme === 'vibrant_academic';
  const isDefault = !isClinical && !isWarmAcademic && !isStructuredCourse && !isMinimal && !isHighContrast && !isVibrant;

  if (isStructuredCourse) {
    return {
      theme,
      isClinical: false,
      isWarmAcademic: false,
      isStructuredCourse: true,
      isMinimal: false,
      isHighContrast: false,
      isVibrant: false,
      isDefault: false,
      primaryBtn: 'bg-[#1D4ED8] hover:bg-[#1E40AF] text-white shadow-sm font-bold rounded-xl transition-all active:scale-95 border border-blue-800/30 focus:ring-2 focus:ring-[#1D4ED8]/40',
      secondaryBtn: 'bg-blue-50 hover:bg-blue-100 text-[#1D4ED8] border border-blue-200 font-bold rounded-xl transition-all',
      activeTab: 'bg-[#1D4ED8] text-white shadow-sm font-bold rounded-xl',
      inactiveTab: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 font-medium rounded-xl',
      cardBg: 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-[#1D4ED8] shadow-xs rounded-2xl',
      headerFont: 'font-sans font-extrabold tracking-tight text-slate-900 dark:text-slate-100',
      badge: 'bg-blue-50 dark:bg-blue-950/50 text-[#1D4ED8] dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-bold rounded-md text-[10px] uppercase tracking-wider',
      progressBar: 'bg-[#1D4ED8]',
      accentText: 'text-[#1D4ED8]',
      accentBorder: 'border-[#1D4ED8]',
      heroBannerBg: 'bg-[#1D4ED8] text-white border-blue-800 shadow-sm rounded-2xl',
      cardHeaderBg: 'bg-[#1D4ED8] text-white rounded-t-2xl',
      iconBg: 'bg-[#1D4ED8] text-white rounded-xl',
    };
  }

  if (isWarmAcademic) {
    return {
      theme,
      isClinical: false,
      isWarmAcademic: true,
      isStructuredCourse: false,
      isMinimal: false,
      isHighContrast: false,
      isVibrant: false,
      isDefault: false,
      primaryBtn: 'bg-[#E05638] hover:bg-[#C8472B] text-white shadow-md font-bold rounded-2xl transition-all active:scale-95 border border-[#C8472B]/30 hover:shadow-lg focus:ring-2 focus:ring-[#E05638]/40',
      secondaryBtn: 'bg-[#E6F4F1] hover:bg-[#D2ECE6] text-[#0D6E6E] border border-[#0D6E6E]/30 font-bold rounded-2xl transition-all',
      activeTab: 'bg-[#0D6E6E] text-white shadow-sm font-bold rounded-2xl',
      inactiveTab: 'bg-[#F3F0E6] dark:bg-slate-800 text-[#4A5568] dark:text-slate-300 hover:bg-[#EAE5D8] font-medium rounded-2xl',
      cardBg: 'bg-white dark:bg-slate-900 border-[#E3DFD5] dark:border-slate-800 hover:border-[#0D6E6E]/40 shadow-sm rounded-3xl',
      headerFont: 'font-sans font-bold tracking-tight text-[#2D3748] dark:text-slate-100',
      badge: 'bg-[#E6F4F1] text-[#0D6E6E] border border-[#0D6E6E]/25 font-bold rounded-full',
      progressBar: 'bg-[#0D6E6E]',
      accentText: 'text-[#0D6E6E]',
      accentBorder: 'border-[#0D6E6E]',
      heroBannerBg: 'bg-[#0D6E6E] text-white border-[#0B5C5C] shadow-sm rounded-3xl',
      cardHeaderBg: 'bg-[#0D6E6E] text-white rounded-t-3xl',
      iconBg: 'bg-[#0D6E6E] text-white rounded-2xl',
    };
  }

  if (isClinical) {
    return {
      theme,
      isClinical,
      isWarmAcademic: false,
      isStructuredCourse: false,
      isMinimal,
      isHighContrast,
      isVibrant,
      isDefault,
      primaryBtn: 'bg-[#0F7D6C] hover:bg-[#0B6356] text-white shadow-md active:scale-95 font-bold transition-all',
      secondaryBtn: 'bg-teal-50 hover:bg-teal-100 text-[#0F7D6C] border border-teal-200 font-bold transition-all',
      activeTab: 'bg-[#0F7D6C] text-white shadow-md font-bold',
      inactiveTab: 'bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium',
      cardBg: 'bg-white border-slate-200 hover:border-teal-600/40 shadow-sm',
      headerFont: 'font-serif font-bold text-[#0B2942]',
      badge: 'bg-teal-50 text-[#0F7D6C] border border-teal-200 font-bold',
      progressBar: 'bg-[#0F7D6C]',
      accentText: 'text-[#0F7D6C]',
      accentBorder: 'border-[#0F7D6C]',
      heroBannerBg: 'bg-[#0B2942] text-white border-slate-800',
      cardHeaderBg: 'bg-[#0B2942] text-white',
      iconBg: 'bg-[#0F7D6C] text-white',
    };
  }

  if (isMinimal) {
    return {
      theme,
      isClinical,
      isWarmAcademic: false,
      isStructuredCourse: false,
      isMinimal,
      isHighContrast,
      isVibrant,
      isDefault,
      primaryBtn: 'bg-slate-800 hover:bg-slate-900 text-white shadow-none font-bold transition-all',
      secondaryBtn: 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 font-semibold transition-all',
      activeTab: 'bg-slate-800 text-white font-bold',
      inactiveTab: 'bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium',
      cardBg: 'bg-white border-slate-300 shadow-none',
      headerFont: 'font-sans font-bold tracking-tight text-slate-900',
      badge: 'bg-slate-100 text-slate-800 border border-slate-300 font-semibold',
      progressBar: 'bg-slate-800',
      accentText: 'text-slate-800',
      accentBorder: 'border-slate-800',
      heroBannerBg: 'bg-slate-900 text-white border-slate-800',
      cardHeaderBg: 'bg-slate-100 text-slate-900 border-b border-slate-200',
      iconBg: 'bg-slate-800 text-white',
    };
  }

  if (isHighContrast) {
    return {
      theme,
      isClinical,
      isWarmAcademic: false,
      isStructuredCourse: false,
      isMinimal,
      isHighContrast,
      isVibrant,
      isDefault,
      primaryBtn: 'bg-amber-400 hover:bg-amber-500 text-black font-black border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all',
      secondaryBtn: 'bg-white hover:bg-slate-100 text-black font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all',
      activeTab: 'bg-black text-amber-400 font-black border-2 border-black',
      inactiveTab: 'bg-white text-black font-bold border-2 border-black hover:bg-amber-100',
      cardBg: 'bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]',
      headerFont: 'font-black tracking-wide text-black',
      badge: 'bg-amber-300 text-black border-2 border-black font-black',
      progressBar: 'bg-amber-500',
      accentText: 'text-black font-black',
      accentBorder: 'border-2 border-black',
      heroBannerBg: 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]',
      cardHeaderBg: 'bg-black text-white border-b-2 border-black',
      iconBg: 'bg-amber-400 text-black border-2 border-black',
    };
  }

  if (isVibrant) {
    return {
      theme,
      isClinical,
      isWarmAcademic: false,
      isStructuredCourse: false,
      isMinimal,
      isHighContrast,
      isVibrant,
      isDefault,
      primaryBtn: 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-md font-bold transition-all',
      secondaryBtn: 'bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 font-bold transition-all',
      activeTab: 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md font-bold',
      inactiveTab: 'bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium',
      cardBg: 'bg-white border-violet-100 hover:border-violet-300 shadow-md',
      headerFont: 'font-black text-violet-950 tracking-tight',
      badge: 'bg-violet-100 text-violet-800 border border-violet-200 font-bold',
      progressBar: 'bg-gradient-to-r from-violet-600 to-indigo-600',
      accentText: 'text-violet-600',
      accentBorder: 'border-violet-600',
      heroBannerBg: 'bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-950 text-white border-violet-800',
      cardHeaderBg: 'bg-gradient-to-r from-violet-900 to-indigo-900 text-white',
      iconBg: 'bg-violet-600 text-white',
    };
  }

  // Default Theme
  return {
    theme: 'default',
    isClinical: false,
    isWarmAcademic: false,
    isStructuredCourse: false,
    isMinimal: false,
    isHighContrast: false,
    isVibrant: false,
    isDefault: true,
    primaryBtn: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-bold transition-all',
    secondaryBtn: 'bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border border-indigo-100 font-bold transition-all',
    activeTab: 'bg-indigo-600 text-white shadow-md font-bold',
    inactiveTab: 'bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium',
    cardBg: 'bg-white border-slate-200 hover:border-slate-300 shadow-sm',
    headerFont: 'font-black text-slate-900',
    badge: 'bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold',
    progressBar: 'bg-indigo-600',
    accentText: 'text-indigo-600',
    accentBorder: 'border-indigo-600',
    heroBannerBg: 'bg-slate-900 text-white border-slate-800',
    cardHeaderBg: 'bg-slate-900 text-white',
    iconBg: 'bg-indigo-600 text-white',
  };
}

export function useActiveTheme(): string {
  const [activeTheme, setActiveTheme] = useState<string>(() => {
    const theme = SponsoredService.getConfig().activeTheme || 'default';
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }
    return theme;
  });

  useEffect(() => {
    const handleUpdate = () => {
      const cfg = SponsoredService.getConfig();
      const newTheme = cfg.activeTheme || 'default';
      setActiveTheme(newTheme);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', newTheme);
      }
    };

    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  return activeTheme;
}

export function useThemeTokens(): ThemeTokens {
  const theme = useActiveTheme();
  return getThemeTokens(theme);
}
